// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/fiscal/hooks/useNotaFiscal.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notaFiscalService, type ListaNotasFiltros } from '../services/notaFiscalService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CancelarNotaRequest, CriarNotaFiscalRequest } from '../types';

const KEYS = {
  all:     ['notas-fiscais'] as const,
  lista:   (p: number, t: number, filtros: ListaNotasFiltros) => ['notas-fiscais', 'lista', p, t, filtros] as const,
  detalhe: (id: number)           => ['notas-fiscais', id] as const,
};

export const useNotaFiscal = (pagina = 1, tamanhoPagina = 20, filtros: ListaNotasFiltros = {}) => {
  const qc = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: KEYS.lista(pagina, tamanhoPagina, filtros),
    queryFn:  () => notaFiscalService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

  const notas        = paginado?.itens        ?? [];
  const total        = paginado?.total         ?? 0;
  const totalPaginas = paginado?.totalPaginas  ?? 1;

  const transmitirMutation = useMutation({
    mutationFn: (id: number) => notaFiscalService.transmitir(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) {
        toast.success(`NF-e transmitida — Protocolo: ${res.protocolo ?? '-'}`);
      } else {
        toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
      }
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cancelarMutation = useMutation({
    mutationFn: ({ id, req }: { id: number; req: CancelarNotaRequest }) =>
      notaFiscalService.cancelar(id, req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) {
        toast.success('NF-e cancelada com sucesso', { description: res.aviso ?? undefined, duration: res.aviso ? 12000 : undefined });
      } else {
        toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
      }
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    notas, total, totalPaginas, isLoading, isFetching,
    transmitir:     transmitirMutation.mutate,
    cancelar:       cancelarMutation.mutate,
    isTransmitindo: transmitirMutation.isPending,
    isCancelando:   cancelarMutation.isPending,
  };
};

export const useNotaFiscalDetalhe = (id: number) =>
  useQuery({
    queryKey: KEYS.detalhe(id),
    queryFn:  () => notaFiscalService.getById(id),
    enabled:  id > 0,
  });

export const useCriarNotaFiscal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CriarNotaFiscalRequest) => notaFiscalService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
    onError:   (e) => toast.error(getApiErrorMessage(e)),
  });
};

export const useImpostosResumo = (dtInicio?: string, dtFim?: string) =>
  useQuery({
    queryKey: ['impostos-resumo', dtInicio, dtFim],
    queryFn:  () => notaFiscalService.getResumoImpostos(dtInicio, dtFim),
  });
